import type { AppointmentService } from '@/api/customer/customerSaleQuery/types';
import {
  type AppointmentDetails,
  AppointmentStatus,
} from '@/types/appointment';

export function filterCancelledAppointments(
  appointments: (AppointmentDetails | undefined | null)[],
): AppointmentDetails[] {
  return appointments.filter(
    (appointment): appointment is AppointmentDetails =>
      appointment != null && appointment.status !== AppointmentStatus.Cancelled,
  );
}

/**
 * Whether a customer-appointment payload contains at least one non-cancelled
 * appointment (the main appointment or any upcoming one). Use this instead of a
 * raw `!!data?.appointment` check so a cancelled appointment is not treated as
 * an active booking — this keeps the home content and the route guards
 * consistent for post-booking guests (KYC) whose appointment was cancelled.
 */
export function hasActiveAppointment(
  data:
    | {
        appointment?: AppointmentDetails | null;
        upcomingAppointments?: (AppointmentDetails | null | undefined)[] | null;
      }
    | null
    | undefined,
): boolean {
  return (
    filterCancelledAppointments([
      data?.appointment,
      ...(data?.upcomingAppointments || []),
    ]).length > 0
  );
}

/**
 * Non-cancelled appointments from a customer-appointment payload, deduped by
 * `id` and sorted ascending by `dateTime` (nearest first).
 *
 * `appointment` (the backend's "nearest" pointer) can already be present as
 * an entry inside `upcomingAppointments` — naively prepending it then would
 * double-count that slot and silently drop a real appointment from any
 * fixed-size slice of the result (SQR-15858).
 */
export function getUpcomingAppointmentsSorted(
  data:
    | {
        appointment?: AppointmentDetails | null;
        upcomingAppointments?: (AppointmentDetails | null | undefined)[] | null;
      }
    | null
    | undefined,
): AppointmentDetails[] {
  const upcomingAppointments = data?.upcomingAppointments || [];
  const isAppointmentAlreadyListed = upcomingAppointments.some(
    (appointment) => appointment?.id === data?.appointment?.id,
  );

  const appointments = filterCancelledAppointments([
    ...(isAppointmentAlreadyListed ? [] : [data?.appointment]),
    ...upcomingAppointments,
  ]);

  return appointments.sort(
    (a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime(),
  );
}

/**
 * Price to display for a single booked service line item.
 *
 * The backend stores `cost` as the tax-INCLUSIVE amount and derives
 * `costWithoutTaxes` as `cost - taxAmount`. Which of the two is the correct
 * customer-facing figure depends on how the shop's taxes are configured:
 *
 * - Any enabled tax ADDED on top of the price — that tax is listed separately
 *   in the totals section, so the line item must show `costWithoutTaxes` to
 *   avoid double-counting it (SQR-13869).
 * - Otherwise (taxes INCLUDED in the price, or no tax at all) — `cost` is what
 *   the customer was quoted and charged, so show it. Showing `costWithoutTaxes`
 *   here understates the price (SQR-14679).
 *
 * Ported from the web app's `getPriceDraftFromSaleOrderSubItem`, which gates on
 * the per-tax `addToPrice` flags rather than a single boolean. A service can
 * carry both an included and an added tax at once, so `taxes[]` is the reliable
 * signal; the flattened `taxAddToPrice` scalar cannot represent that case and
 * is only used as a fallback when `taxes[]` is absent.
 */
export function getServiceDisplayCost(service: AppointmentService): number {
  const hasAddedTax = service.taxes
    ? service.taxes.some((tax) => !tax.exclude && tax.addToPrice)
    : !!service.taxAddToPrice;

  return hasAddedTax ? service.costWithoutTaxes : service.cost;
}
